import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, ChevronDown, ChevronLeft, ExternalLink, MapPin } from 'lucide-react';
import { api } from '../lib/api';
import { isSatur, withUtm } from '../lib/utm';
import { useLang } from '../i18n';
import Seo from '../components/Seo';
import { ShareBar } from '../components/ShareBar';
import { ProductTile } from '../components/ProductTile';
import { Img } from '../components/Img';
import { Reveal, Stagger, StaggerItem } from '../components/Reveal';
import { collectionCover } from '../lib/collectionCovers';

const FALLBACK =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='600' height='600'><rect width='600' height='600' fill='%23EFE9E0'/><text x='300' y='320' font-family='serif' font-size='62' fill='%23B08A74' text-anchor='middle'>VdE</text></svg>";

const CARE = {
  it: {
    Porcellana: 'Lavabile in lavastoviglie. Adatto al microonde, senza decori metallici.',
    Gres: 'Lavabile in lavastoviglie e adatto al microonde. Evita gli sbalzi termici bruschi.',
    Vetro: 'Lavabile in lavastoviglie a bassa temperatura. Non usare spugne abrasive.',
    Melamina: 'Lavabile a mano o in lavastoviglie a bassa temperatura. Mai nel microonde.',
    Legno: 'Pulisci con un panno umido, asciuga subito. Non in lavastoviglie.',
    Ferro: 'Panno asciutto. Evita l’immersione prolungata in acqua.',
    Ceramica: 'Lavabile in lavastoviglie. Il decoro va protetto dalle spugne abrasive.',
    default: 'Panno morbido e acqua tiepida. Tratta i decori con la cura che meritano.',
  },
  en: {
    Porcellana: 'Dishwasher safe. Microwave safe, unless it has metallic decoration.',
    Gres: 'Dishwasher and microwave safe. Avoid sudden temperature changes.',
    Vetro: 'Dishwasher safe at low temperature. Don’t use abrasive sponges.',
    Melamina: 'Hand wash or low-temperature dishwasher. Never in the microwave.',
    Legno: 'Wipe with a damp cloth and dry immediately. Not dishwasher safe.',
    Ferro: 'Dry cloth. Avoid prolonged soaking.',
    Ceramica: 'Dishwasher safe. Protect the decoration from abrasive sponges.',
    default: 'Soft cloth and lukewarm water. Treat the decoration with the care it deserves.',
  },
};

// Tre tessere in un rigo da quattro lasciavano una colonna vuota (10 schede su
// 60 dopo il filtro duplicati): con tre si va a tre colonne.
const colonne = (n) => (n === 3 ? 'md:grid-cols-3' : 'md:grid-cols-4');

// Il feed ha varianti (misura/colore) con lo stesso titolo: nei correlati
// mostrarne due di fila legge come un errore ("stesso prodotto ripetuto"),
// non come un suggerimento. Se ne tiene solo una per titolo.
const unicoPerTitolo = (arr = []) => {
  const visti = new Set();
  return arr.filter((s) => {
    if (visti.has(s.title)) return false;
    visti.add(s.title);
    return true;
  });
};

/**
 * Divide il racconto in apertura (una frase, in Bodoni corsivo) e corpo (Archivo).
 *
 * Misurato su 48 note emozionali (17/10): la prima frase sta entro 18 parole
 * in 28 note. Oltre e' gia' un paragrafo, non un'apertura: allora niente
 * corsivo e tutto il testo in Archivo. Una nota di una frase sola resta tutta
 * corpo. La frase finisce a . ! ? o ... seguiti da spazio e maiuscola, cosi'
 * "h. 10 cm" non spezza.
 */
export const apriRacconto = (testo) => {
  const pulito = (testo || '').trim();
  const m = pulito.match(/^([\s\S]+?[.!?…])\s+(?=[A-ZÀ-Ý«"])/);
  if (!m) return { apertura: null, corpo: pulito };
  const apertura = m[1];
  if (apertura.split(/\s+/).length > 18) return { apertura: null, corpo: pulito };
  return { apertura, corpo: pulito.slice(m[0].length) };
};

/**
 * Il racconto del prodotto, con la collezione «in tavola» accanto.
 *
 * Prima la nota stava in Bodoni tondo a 35px su nove decimi della larghezza
 * (38 parole in 4 righe, inspect 17/10): un manifesto, non una lettura. Ora
 * una sola frase d'apertura in corsivo e il resto in Archivo a 19px, 60
 * caratteri di riga. Se la collezione ha lo scatto d'ambiente (13 su 102) gli
 * sta accanto a tutta immagine, senza margini ne' multiply: e' l'unica foto
 * della pagina che mostra il pezzo apparecchiato, e porta alla collezione.
 */
const Racconto = ({ apertura, corpo, italianHint, collectionSlug, collection, cover, didascalia }) => {
  const { t } = useLang();
  const testo = apertura || corpo;
  return (
    <section data-testid="product-story" className="bg-carta-2 py-16 md:py-24">
      <div className={`wrap grid gap-12 ${testo && cover ? 'lg:grid-cols-2 lg:items-center lg:gap-20' : ''}`}>
        {testo && (
          <Reveal className="min-w-0">
            {apertura && (
              <p
                data-testid="product-story-opening"
                className="accento max-w-[28ch] text-[clamp(1.6rem,3vw,2.4rem)] leading-[1.22] text-inchiostro"
              >
                {apertura}
              </p>
            )}
            {corpo && (
              <p
                data-testid="product-story-body"
                className={`max-w-[60ch] text-[19px] leading-[1.62] text-inchiostro/80 [text-wrap:pretty] ${apertura ? 'mt-6' : ''}`}
              >
                {corpo}
              </p>
            )}
            {italianHint && (
              <p className="mt-6 text-[14px] font-bold text-inchiostro/70">
                {italianHint}
              </p>
            )}
            {collectionSlug && (
              <Link
                to={`/collezioni/${collectionSlug}`}
                data-testid="product-collection-bridge"
                className="btn btn-line mt-9"
              >
                {t('common.discoverCollection')}
                <ArrowUpRight size={15} strokeWidth={2} />
              </Link>
            )}
          </Reveal>
        )}
        {cover && (
          <Reveal as="figure" delay={0.1} data-testid="product-in-tavola" className="m-0 min-w-0">
            <Link
              to={`/collezioni/${collectionSlug}`}
              className="group block aspect-[5/4] overflow-hidden bg-carta-3"
            >
              <Img
                src={cover}
                alt={collection}
                width={960}
                aspectRatio={5 / 4}
                sizes="(min-width: 1024px) 45vw, 100vw"
                className="img-zoom h-full w-full object-cover"
              />
            </Link>
            {didascalia && (
              <figcaption className="mt-4 max-w-[60ch] text-[14px] leading-relaxed text-inchiostro/70 [text-wrap:pretty]">
                {didascalia}
              </figcaption>
            )}
          </Reveal>
        )}
      </div>
    </section>
  );
};

/**
 * Il cassetto tecnico: misure, peso, codice e note, aperti quando servono.
 *
 * Prima la scheda teneva sette righe di tabella sempre aperte (554px su
 * telefono) piu' questo pannello: due blocchi per la stessa materia, e il
 * sommario del pannello ("Misure, materiali, dettagli") prometteva proprio
 * quello che la tabella sopra mostrava gia'. Ora in pagina restano le tre cose
 * che una persona chiede davvero — di che materia e', di che colore, come si
 * lava — e i numeri stanno qui dentro, a un tocco.
 */
const TechnicalNote = ({ lines, specs = [] }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-8" data-testid="product-technical-note">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        data-testid="product-technical-toggle"
        className="group flex w-full items-center justify-between gap-6 py-5 text-left"
      >
        <span>
          <span className="label-wide block text-inchiostro transition-colors group-hover:text-marsala">
            {t('common.technicalNote')}
          </span>
          <span className="mt-1 block text-[14px] text-inchiostro/70">
            {t('common.technicalNoteHint')}
          </span>
        </span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.35 }}>
          <ChevronDown size={18} strokeWidth={1.6} className="text-marsala" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="tech"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            {specs.length > 0 && (
              <dl className="mb-6">
                {specs.map(([k, v]) => (
                  <div
                    key={k}
                    className="grid gap-1 py-3 md:grid-cols-[150px_1fr] md:gap-6"
                  >
                    <dt className="label !mb-0 md:pt-1">{k}</dt>
                    <dd className="text-base leading-relaxed text-inchiostro/80">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
            <ul data-testid="product-technical-list" className="space-y-2.5 pb-7">
              {lines.map((l, i) => (
                <li key={`${i}-${l.slice(0, 18)}`} className="flex gap-3 text-base leading-relaxed text-inchiostro/75">
                  <span>{l}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Product() {
  const { slug } = useParams();
  const { t, lang } = useLang();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [notFound, setNotFound] = useState(false);
  const galleria = useRef(null);

  // Le posizioni si misurano coi rettangoli, non con `offsetLeft`: l'`ul` non e'
  // un contenitore di posizionamento, quindi `offsetLeft` viene contato dal body
  // e su schermo largo — dove la pista rientra nel margine di pagina — l'errore
  // e' costante: la miniatura evidenziata indicava la foto sbagliata.
  const scorrimentoPer = (el, i) =>
    el.scrollLeft + el.children[i].getBoundingClientRect().left - el.getBoundingClientRect().left;

  // la miniatura porta alla foto; scorrendo col dito, l'attiva segue la pista
  const vaiA = (i) => {
    const el = galleria.current;
    if (el?.children[i]) el.scrollTo({ left: scorrimentoPer(el, i), behavior: 'smooth' });
    setActive(i);
  };
  const onScrollGalleria = () => {
    const el = galleria.current;
    if (!el) return;
    let best = 0;
    let d = Infinity;
    for (let i = 0; i < el.children.length; i += 1) {
      const dist = Math.abs(scorrimentoPer(el, i) - el.scrollLeft);
      if (dist < d) {
        d = dist;
        best = i;
      }
    }
    setActive(best);
  };

  useEffect(() => {
    setP(null);
    setActive(0);
    setNotFound(false);
    if (galleria.current) galleria.current.scrollLeft = 0;
    api
      .get(`/products/${slug}?lang=${lang}`)
      .then((r) => setP(r.data))
      .catch(() => setNotFound(true));
  }, [slug, lang]);

  if (notFound) {
    return (
      <div className="wrap py-32 text-center">
        <h1 className="text-4xl">404</h1>
        <Link to="/collezioni" className="btn btn-line mt-8">
          {t('common.allCollections')}
        </Link>
      </div>
    );
  }

  if (!p) {
    return (
      <div className="wrap py-24">
        <div className="grid gap-12 lg:grid-cols-2">
          <div className="aspect-square bg-carta-2" />
          <div className="space-y-4">
            <div className="h-3 w-24 bg-carta-3" />
            <div className="h-10 w-3/4 bg-carta-2" />
            <div className="h-24 bg-carta-2" />
          </div>
        </div>
      </div>
    );
  }

  const images = p.images?.length ? p.images : [FALLBACK];
  const cleanTitle = p.title.replace(/,\s*[^,]+$/, '');
  // il feed porta solo link satur.it; se un domani ne arrivasse uno diverso, niente etichetta satur.it
  const onSatur = isSatur(p.retailer_url);
  const careMap = CARE[lang] || CARE.it;
  // Il legno vince sugli altri materiali: un barattolo bambu + ceramica non va in lavastoviglie.
  const mats = p.it?.materials || p.materials || []; // la cura si sceglie sul materiale del feed
  const care = (mats.some((m) => /legno|bamb|acacia/i.test(m)) && careMap.Legno)
    || mats.map((m) => careMap[m]).find(Boolean) || careMap.default;
  /**
   * Il feed apre la nota tecnica con cose che la pagina dice gia': la prima riga
   * e' una parafrasi del titolo e la seconda il materiale ("In porcellana" sopra
   * un "MATERIALI: Porcellana"). Il confronto per stringa non basta — la riga era
   * "Servizio piatti 18 pezzi The Blue" contro un titolo "Servizio piatti 18
   * pezzi in porcellana" — quindi si guarda il vocabolario: una riga breve le cui
   * parole stanno TUTTE gia' in titolo, collezione o materiali non aggiunge
   * niente a chi apre il cassetto. Il limite di otto parole protegge le frasi
   * informative che riusano qualche termine.
   */
  const norm = (v) =>
    (v || '')
      .toLowerCase()
      .replace(/[^a-z0-9àèéìòù\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  const parole = (v) =>
    (v || '')
      .toLowerCase()
      .replace(/[^a-z0-9àèéìòù\s]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);
  const vocabolario = new Set([
    ...parole(p.title),
    ...parole(p.collection),
    ...(p.materials || []).flatMap(parole),
  ]);
  const ripeteLaPagina = (l) => {
    const w = parole(l);
    return w.length > 0 && w.length <= 8 && w.every((x) => vocabolario.has(x));
  };
  const noteTecniche = (p.nota_tecnica || []).filter((l) => !ripeteLaPagina(l));

  const dettagli = [
    [
      t('common.dimensions'),
      p.dimensions?.length ? p.dimensions.map((d) => `${d.label} ${d.value}`).join(' · ') : null,
    ],
    [t('common.weight'), p.weight],
    [t('common.shade'), p.shade],
  ].filter(([, v]) => v);
  const shareUrl = `${window.location.origin}/prodotti/${p.slug}`;
  const italianHint = lang === 'en' ? t('common.noteInItalian') : null;
  // Una storia sola per pagina. Vince la nota del prodotto (c'e' in 48 schede su
  // 60 e parla di quel pezzo); il racconto della collezione, uguale su tutti i
  // pezzi della serie, fa da ripiego: il claim diventa l'apertura. In inglese
  // la nota e' solo in italiano, la storia della collezione e' tradotta: vince lei.
  const storia = p.collection_story?.story ? p.collection_story : null;
  const racconto = p.nota_emozionale && !(lang === 'en' && storia)
    ? { ...apriRacconto(p.nota_emozionale), fonte: 'nota' }
    : storia && { apertura: storia.claim || null, corpo: storia.story, fonte: 'collezione' };
  const cover = collectionCover(p.collection_slug, '-54');
  // sotto lo scatto: il nome della collezione, piu' il claim se non e' gia' l'apertura
  const didascalia = [
    p.collection,
    racconto?.fonte === 'nota' ? storia?.claim : null,
    ['ue-ue', 'tinte-uniche'].includes(p.collection_slug) ? t('common.storyCoverCaptionGenerated') : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <Seo
        title={cleanTitle}
        suffix=" | VdE"
        description={`${p.description} · ${p.materials?.join(', ')}.`}
        image={images[0]}
        path={`/prodotti/${p.slug}`}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: p.title,
          description: p.nota_emozionale || p.description,
          sku: p.sku,
          gtin13: p.gtin || undefined,
          image: images,
          material: p.materials?.join(', '),
          color: p.colors?.join(', '),
          brand: { '@type': 'Brand', name: 'VdE Tivoli 1996' },
          isRelatedTo: p.collection || undefined,
        }}
      />

      <div className="wrap pt-6">
        <nav
          aria-label="breadcrumb"
          className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-inchiostro/70"
        >
          <Link to="/collezioni" className="inline-flex min-h-[44px] items-center gap-1 hover:text-marsala">
            <ChevronLeft size={12} /> {t('nav.collections')}
          </Link>
          {p.collection_slug ? (
            <>
              <span>/</span>
              <Link to={`/collezioni/${p.collection_slug}`} className="hover:text-marsala">
                {p.collection}
              </Link>
            </>
          ) : (
            p.category_slug && (
              <>
                <span>/</span>
                <Link to={`/catalogo/${p.category_slug}`} className="hover:text-marsala">
                  {(p.category_label || p.category)?.toLowerCase()}
                </Link>
              </>
            )
          )}
          <span>/</span>
          <span className="text-inchiostro/60">{cleanTitle}</span>
        </nav>
      </div>

      <section className="py-8 md:py-14">
        <div className="wrap grid min-w-0 gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-20">
          {/*
            Telefono e tablet: pista con snap, una foto per volta, miniature su
            una riga sola che scorre (fino a 29 foto non vanno piu' a capo).
            Da lg la stessa lista diventa un mosaico a due colonne con TUTTE le
            foto: l'82% dei prodotti ne ha 3 o piu' (mediana 4, misurato su
            1557 il 17/10) e la vetrina a una foto le nascondeva. La prima e'
            grande; l'ultima, se resterebbe sola nel rigo, anche. Le miniature
            da lg spariscono: si vede gia' tutto.
          */}
          <div className="min-w-0">
            <ul
              ref={galleria}
              onScroll={onScrollGalleria}
              data-testid="product-gallery"
              className="-mx-[var(--gutter)] flex snap-x snap-mandatory overflow-x-auto fila lg:mx-0 lg:grid lg:snap-none lg:grid-cols-2 lg:gap-3 lg:overflow-visible"
            >
              {images.map((src, i) => {
                const grande = i === 0 || (i === images.length - 1 && i % 2 === 1);
                return (
                  <Reveal
                    as="li"
                    key={src}
                    delay={(i % 4) * 0.05}
                    className={`aspect-square w-full shrink-0 snap-center bg-carta-2 ${grande ? 'lg:col-span-2' : ''}`}
                    data-testid={i === 0 ? 'product-main-image' : `product-image-${i}`}
                  >
                    <Img
                      src={src}
                      alt={i === 0 ? cleanTitle : `${cleanTitle}, ${i + 1}`}
                      width={960}
                      sizes={grande ? '(min-width: 1024px) 50vw, 100vw' : '(min-width: 1024px) 25vw, 100vw'}
                      priority={i === 0}
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK;
                      }}
                      className={`h-full w-full object-contain p-5 mix-blend-multiply md:p-16 ${grande ? '' : 'lg:p-8'}`}
                    />
                  </Reveal>
                );
              })}
            </ul>
            {images.length > 1 && (
              <div className="fila mt-4 flex gap-3 overflow-x-auto lg:hidden">
                {images.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    onClick={() => vaiA(i)}
                    aria-label={t('common.thumbAlt')(i + 1)}
                    aria-current={i === active}
                    data-testid={`product-thumb-${i}`}
                    data-ui className={`h-20 w-20 shrink-0 overflow-hidden border transition-colors ${i === active ? 'border-marsala' : 'border-inchiostro/12 hover:border-inchiostro/40'}`}
                  >
                    <Img
                      src={src}
                      alt=""
                      width={160}
                      onError={(e) => {
                        e.currentTarget.src = FALLBACK;
                      }}
                      className="h-full w-full object-contain p-2 mix-blend-multiply"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* scheda */}
          <Reveal className="min-w-0" delay={0.1}>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[3.4rem]">{cleanTitle}</h1>
            {p.collection && (
              <Link
                to={`/collezioni/${p.collection_slug}`}
                data-testid="product-collection-link"
                className="link-ul mt-3 inline-block text-[14px]"
              >
                {p.collection}
              </Link>
            )}
            {/*
              La descrizione del feed ricomincia dal titolo: sotto un h1 "Servizio
              piatti 18 pezzi in porcellana" scriveva "Servizio piatti 18 pezzi in
              porcellana, 6 posti tavola, The Blue". Il confronto di prima chiedeva
              l'uguaglianza esatta e quindi non la vedeva. Ora se riparte dal
              titolo non si stampa: quello che aggiungeva (i posti tavola, la
              collezione) sta nel cassetto tecnico e nel breadcrumb.
            */}
            {p.description && !norm(p.description).startsWith(norm(cleanTitle)) && (
              <p className="prosa mt-6 text-base">{p.description}</p>
            )}

            {/* In pagina le tre cose che una persona chiede davvero. Misure, peso
                e shade scendono nel cassetto tecnico: erano sette righe sempre
                aperte, e "codice" ripeteva lo SKU che il breadcrumb mostra in
                cima, "categoria" era un percorso scritto che non si poteva
                cliccare (ora e' un link nel breadcrumb). */}
            <dl className="mt-9" data-testid="product-specs">
              {[
                [t('common.materials'), (p.materials || []).join(' · ')],
                [t('common.colors'), (p.colors || []).join(' · ')],
                [t('common.care'), care],
              ]
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <div
                    key={k}
                    className="grid gap-1 py-4 md:grid-cols-[150px_1fr] md:gap-6"
                  >
                    <dt className="label !mb-0 md:pt-1">{k}</dt>
                    <dd className="text-base leading-relaxed text-inchiostro/80">{v}</dd>
                  </div>
                ))}
            </dl>

            {(noteTecniche.length > 0 || dettagli.length > 0) && (
              <TechnicalNote lines={noteTecniche} specs={dettagli} />
            )}

            {/* Un bottone solo: comprare online. "Trova dove acquistare" e' gia'
                il bottone dell'header, qui resta un link sotto la nota satur.it. */}
            <div className="mt-9 flex flex-col items-stretch gap-4 sm:items-start">
              {p.retailer_url && (
                <a
                  href={onSatur ? withUtm(p.retailer_url) : p.retailer_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="product-retailer-cta"
                  aria-label={
                    onSatur
                      ? `${t('common.buyOnSatur')}, ${t('common.buyOnSaturNote')}`
                      : undefined
                  }
                  className="btn btn-solid"
                >
                  {onSatur ? t('common.buyOnSatur') : t('common.buyProduct')}
                  <ExternalLink size={14} strokeWidth={2} />
                </a>
              )}
              {onSatur && (
                <p className="text-[14px] font-bold text-inchiostro/70">
                  {t('common.buyOnSaturNote')}
                </p>
              )}
              <Link
                to="/dove-comprare"
                data-testid="product-where-cta"
                className="link-ul inline-flex min-h-[44px] items-center gap-2 self-start text-[15px] font-semibold text-inchiostro transition-colors hover:text-marsala"
              >
                <MapPin size={15} strokeWidth={2} />
                {t('common.findRetailer')}
              </Link>
            </div>

            <ShareBar url={shareUrl} title={cleanTitle} image={images[0]} />
          </Reveal>
        </div>
      </section>

      {(racconto || cover) && (
        <Racconto
          apertura={racconto?.apertura}
          corpo={racconto?.corpo}
          italianHint={racconto?.fonte === 'nota' ? italianHint : null}
          collectionSlug={p.collection_slug}
          collection={p.collection}
          cover={cover}
          didascalia={didascalia}
        />
      )}

      {(() => {
        const pairingsUnici = unicoPerTitolo(p.pairings || []);
        // stessa soglia della fascia sotto: una tessera sola (2 schede su 60) non e' un abbinamento
        return (
          pairingsUnici.length > 1 && (
            <section data-testid="product-pairings" className="py-14 md:py-20">
              <div className="wrap">
                <Reveal as="h2" className="accento mb-9 text-2xl text-inchiostro/85">{t('common.pairWith')}</Reveal>
                <Stagger
                  className={`grid grid-cols-2 gap-x-5 gap-y-10 md:gap-x-7 ${colonne(pairingsUnici.length)}`}
                  gap={0.06}
                >
                  {pairingsUnici.map((s) => (
                    <StaggerItem key={s.sku}>
                      <ProductTile product={s} />
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            </section>
          )
        );
      })()}

      {(() => {
        const siblingsUnici = unicoPerTitolo(p.siblings || []).slice(0, 8);
        // La fascia e' pensata per piu' pezzi affiancati: con un solo prodotto
        // (spesso dopo il filtro duplicati) restava una card sola in un
        // rigo pensato per quattro colonne.
        return (
          siblingsUnici.length > 1 && (
            <section data-testid="product-siblings" className="bg-carta-2 py-14 md:py-20">
              <div className="wrap">
                <Reveal as="h2" className="accento mb-9 text-2xl text-inchiostro/85">{t('common.sameCollection')}</Reveal>
                <Stagger
                  className={`grid grid-cols-2 gap-x-5 gap-y-10 md:gap-x-7 ${colonne(siblingsUnici.length)}`}
                  gap={0.06}
                >
                  {siblingsUnici.map((s) => (
                    <StaggerItem key={s.sku}>
                      <ProductTile product={s} />
                    </StaggerItem>
                  ))}
                </Stagger>
              </div>
            </section>
          )
        );
      })()}
    </>
  );
}
